
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	Input,
	Button,
	Card,
	Container,
	Modal,
	Typography,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Box,
	Divider
} from '@mui/material';
import api from '../../../utilities/axios';
import Loading from '../../../components/Loading';

const contactFields = [
	{ label: 'First Name', value: 'firstName' },
	{ label: 'Last Name', value: 'lastName' },
	{ label: 'Email', value: 'email' },
	{ label: 'Phone', value: 'phone' },
	{ label: 'Company Name', value: 'companyName' },
	{ label: 'Address', value: 'address' },
	{ label: 'City', value: 'city' },
	{ label: 'State', value: 'state' },
	{ label: 'Country', value: 'country' },
	{ label: 'Zip Code', value: 'zipCode' },
	{ label: 'Web Address', value: 'webAddress' },
	{ label: 'Birthday', value: 'birthday' },
	{ label: 'Rating', value: 'rating' },
	{ label: 'Engagement', value: 'engagement' },
	{ label: 'Source', value: 'source' },
];

const CampaignEmailBuilder = () => {
	const { id } = useParams();
	const [campaign, setCampaign] = useState(null);
	const [template, setTemplate] = useState({
		templateHtml: '',
		templateJson: {},
	});
	const [modalOpen, setModalOpen] = useState(false);
	const [placeholderValues, setPlaceholderValues] = useState({});

	const handleSubmitMappings = async () => {
		await api.patch(`/user/campaigns/${id}`, {
			templateMappings: placeholderValues,
		});
		setModalOpen(false);
	};

	const getCampaignsData = async () => {
		if (!id) return;
		try {
			const { data } = await api.get( `/user/campaigns/details/${id}`);
			setCampaign(data.data);
			setTemplate({
				templateHtml: data.data.template?.templateHtml || '',
				templateJson: data.data.template?.templateJson || {},
			});
			
			const placeholders = data.data.template?.placeholders || [];
			const existingMappings = data.data.templateMappings || {};
			
			const initialMappings = placeholders.reduce((acc, placeholder) => {
				// 1. Check if we already have a saved mapping
				if (existingMappings[placeholder]) {
					acc[placeholder] = existingMappings[placeholder];
				} 
				// 2. Otherwise, check if the placeholder name exactly matches a contact field value
				else if (contactFields.some(f => f.value === placeholder)) {
					acc[placeholder] = placeholder;
				}
				// 3. Otherwise, check if the placeholder name matches a contact field label (lowercase, no spaces)
				else {
					const normalizedPlaceholder = placeholder.toLowerCase().replace(/\s/g, '');
					const matchedField = contactFields.find(f => 
						f.label.toLowerCase().replace(/\s/g, '') === normalizedPlaceholder
					);
					acc[placeholder] = matchedField ? matchedField.value : '';
				}
				return acc;
			}, {});

			setPlaceholderValues(initialMappings);
		} catch (err) {
			console.log(err);
		}
	};

	useEffect(() => {
		getCampaignsData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (!campaign) return <Loading/>;
	return (
		<div>
			<div className='flex items-center justify-between gap-4 mb-4'>
				<Typography variant='h5'>{campaign.name}</Typography>
				<Button
					size='small'
					color='secondary'
					variant='contained'
					onClick={() => setModalOpen(true)}
				>
					Create Mapping
				</Button>
			</div>

			<Modal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<Container maxWidth='sm'>
					<Card style={{ width: '100%', padding: '32px' }}>
						<Typography variant='h5'>Create Mapping</Typography>
						<Typography>
							Create a mapping of variables between your template and the
							entries of selected tag/segment
						</Typography>

						{(campaign.template?.placeholders || []).map((placeholder) => {
							return (
								<Box key={placeholder} sx={{ mb: 3 }}>
									<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
										Placeholder: <code>{`{{${placeholder}}}`}</code>
									</Typography>
									<FormControl fullWidth size="small">
										<InputLabel>Map to Field</InputLabel>
										<Select
											label="Map to Field"
											value={placeholderValues[placeholder] || ''}
											onChange={(e) =>
												setPlaceholderValues((prev) => ({
													...prev,
													[placeholder]: e.target.value,
												}))
											}
										>
											<MenuItem value=""><em>Do not map</em></MenuItem>
											{contactFields.map((field) => (
												<MenuItem key={field.value} value={field.value}>
													{field.label}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Box>
							);
						})}

						<Button
							size='small'
							type='submit'
							color='secondary'
							variant='contained'
							onClick={handleSubmitMappings}
						>
							Confirm
						</Button>
					</Card>
				</Container>
			</Modal>

			<iframe
				title='Template'
				style={{ width: '100%', height: '500px' }}
				srcDoc={template.templateHtml ? template.templateHtml : "<h1>Template</h1>"}
			/>
		</div>
	);
};

export default CampaignEmailBuilder;
